import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, Container, Snackbar, Alert } from '@mui/material';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AddRoom from './pages/AddRoom';
import Navbar from './components/Navbar';
import AdminPanel from "./pages/AdminPanel";
import Reports from './pages/Reports';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
    },
    secondary: {
      main: '#0d47a1',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [wsConnected, setWsConnected] = useState(false);
  const stompClientRef = useRef(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    if (!user) return;

    const client = new Client({
      webSocketFactory: () => new SockJS('/ws'),
      reconnectDelay: 5000,
      onConnect: () => {
        setWsConnected(true);
        console.log('WebSocket connected');
      },
      onDisconnect: () => {
        setWsConnected(false);
        console.log('WebSocket disconnected');
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        setWsConnected(false);
      },
    });

    client.activate();
    stompClientRef.current = client;

    return () => {
      if (client.active) {
        client.deactivate();
      }
    };
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    if (stompClientRef.current?.active) {
      stompClientRef.current.deactivate();
    }
  };

  if (!user) {
    return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Login onLogin={handleLogin} />
        </ThemeProvider>
    );
  }

  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Navbar user={user} onLogout={handleLogout} wsConnected={wsConnected} />
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Routes>
                <Route
                    path="/"
                    element={
                      <Dashboard
                          user={user}
                          stompClient={stompClientRef.current}
                          showToast={showToast}
                      />
                    }
                />
                <Route
                    path="/add-room"
                    element={
                      user.role === 'ADMIN' || user.role === 'MANAGER'
                          ? <AddRoom showToast={showToast} />
                          : <Navigate to="/" />
                    }
                />
                <Route
                    path="/admin"
                    element={
                      user.role === 'ADMIN'
                          ? <AdminPanel showToast={showToast} />
                          : <Navigate to="/" />
                    }
                />
                <Route path="/login" element={<Navigate to="/" />} />
                <Route path="*" element={<Navigate to="/" />} />
                <Route
                    path="/reports"
                    element={
                      user.role === 'ADMIN' || user.role === 'MANAGER'
                          ? <Reports />
                          : <Navigate to="/" />
                    }
                />
              </Routes>
            </Container>
          </Box>
          <Snackbar
              open={toast.open}
              autoHideDuration={4000}
              onClose={() => setToast({ ...toast, open: false })}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
                onClose={() => setToast({ ...toast, open: false })}
                severity={toast.severity}
                variant="filled"
                sx={{ borderRadius: 2, fontWeight: 500 }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Router>
      </ThemeProvider>
  );
}

export default App;
